import { FC, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { trainingConstructorStore } from '@pages/TrainingConstructor/stores'
import Modal from 'react-modal'
import { AnimatePresence, motion } from 'framer-motion'
import { ExerciseParams } from '@pages/TrainingConstructor/types'
import { ExercisesAccordion } from './exercisesAccordion'
import { useCreateTraining } from '@entities/training/hooks/crud.create'
import { CreateTrainingResponse } from '@entities/training/types/create'
import { Bike, Lock, Plus, X } from 'lucide-react'
import { SearchField } from '@ui/SearchField'
import { QuickExerciseFilters } from '@pages/TrainingConstructor/ui/filter/quickFilters'
import { Filters } from '@pages/TrainingConstructor/ui/filter/filters'
import { CreateTraining } from '@pages/TrainingConstructor/ui/CreateTraining'
import { Exercise } from '@entities/exercise'
import { useExerciseFilterStore } from '@entities/exercise/model/filters.provider'
import { ExerciseFilterStoreProvider } from '@entities/exercise/model/filters.provider'
import { ExercisesStoreProvider } from '@entities/exercise/model/dto.provider'
import { buildExerciseParamsFromExercise } from '@pages/TrainingConstructor/lib/buildExerciseParams'
import './trainingFormModal.css'
import '@pages/TrainingConstructor/ui/training-constructor.css'
import '@pages/auth/pages/AuthInfo.css'

interface TrainingFormModalProps {
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
	exercises: ExerciseParams[]
	prevTitle?: string
	prevDescription?: string
	draftTitle?: string
	draftDescription?: string
	onDraftChange?: (draft: { title: string; description: string }) => void
	onUpdate?: (exercises: ExerciseParams[], title: string, description: string) => void
	onDelete?: () => void
	onCreateSuccess?: (training: CreateTrainingResponse) => void
	title?: string
	submitLabel?: string
	hideSecondaryAction?: boolean
}

const TITLE_LIMIT = 60
const DESCRIPTION_LIMIT = 200

const reorderExerciseParams = (params: ExerciseParams[]): ExerciseParams[] =>
	params.map((exercise, index) => ({ ...exercise, order: index + 1 }))

function formatExerciseCount(count: number): string {
	return `Упражнения (${count})`
}

function formatPickerCount(count: number): string {
	if (count === 1) {
		return '1 упражнение в тренировке'
	}

	if (count >= 2 && count <= 4) {
		return `${count} упражнения в тренировке`
	}

	return `${count} упражнений в тренировке`
}

interface ExercisePickerSheetProps {
	isOpen: boolean
	onClose: () => void
	selectedExerciseIds: Set<number>
	onToggleExercise: (exercise: Exercise) => void
}

const ExercisePickerSheetContent: FC<ExercisePickerSheetProps> = observer(
	({ isOpen, onClose, selectedExerciseIds, onToggleExercise }) => {
		const [isFiltersOpen, setIsFiltersOpen] = useState(false)
		const filterStore = useExerciseFilterStore()

		return (
			<>
				<Filters
					isFiltersOpen={isFiltersOpen}
					setIsFiltersOpen={setIsFiltersOpen}
				/>
				<AnimatePresence initial={false}>
					{isOpen && (
						<motion.div
							className="training-exercise-picker-overlay"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={onClose}
						/>
					)}
				</AnimatePresence>
				<AnimatePresence initial={false}>
					{isOpen && (
						<motion.div
							className="training-exercise-picker-sheet training-constructor-page"
							role="dialog"
							aria-modal="true"
							aria-labelledby="training-exercise-picker-title"
							initial={{ x: '-50%', y: '100%', opacity: 0.98 }}
							animate={{ x: '-50%', y: 0, opacity: 1 }}
							exit={{ x: '-50%', y: '100%', opacity: 0.98 }}
							transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
						>
							<div className="training-exercise-picker-handle" aria-hidden="true" />
							<header className="training-exercise-picker-header">
								<div>
									<h2 id="training-exercise-picker-title">
										Добавить упражнение
									</h2>
									<p>{formatPickerCount(selectedExerciseIds.size)}</p>
								</div>
								<button
									type="button"
									className="training-exercise-picker-close"
									onClick={onClose}
									aria-label="Закрыть выбор упражнений"
								>
									<X aria-hidden="true" />
								</button>
							</header>
							<section className="training-exercise-picker-controls">
								<SearchField
									data={filterStore.getFtsSearchTerm()}
									setData={filterStore.setFtsSearchTerm}
									placeholder="Найти упражнение"
									iconPosition="left"
									styles={{ borderRadius: 18 }}
								/>
								<QuickExerciseFilters
									onOpenAdvancedFilters={() => setIsFiltersOpen(true)}
								/>
							</section>
							<main className="training-exercise-picker-main">
								<CreateTraining
									className="training-exercise-picker-list"
									selectedExerciseIds={selectedExerciseIds}
									onToggleExercise={onToggleExercise}
								/>
							</main>
							<footer className="training-exercise-picker-footer">
								<div>
									<strong>{selectedExerciseIds.size}</strong>
									<span>выбрано</span>
								</div>
								<button type="button" onClick={onClose}>
									Готово
								</button>
							</footer>
						</motion.div>
					)}
				</AnimatePresence>
			</>
		)
	},
)

const ExercisePickerSheet: FC<ExercisePickerSheetProps> = (props) => {
	return (
		<ExerciseFilterStoreProvider>
			<ExercisesStoreProvider>
				<ExercisePickerSheetContent {...props} />
			</ExercisesStoreProvider>
		</ExerciseFilterStoreProvider>
	)
}

export const FormTrainingModal: FC<TrainingFormModalProps> = observer(
	({
		isOpen,
		setIsOpen,
		onDelete,
		prevTitle,
		prevDescription,
		draftTitle,
		draftDescription,
		onDraftChange,
		onUpdate,
		exercises,
		onCreateSuccess,
		title: titleProp,
		submitLabel,
		hideSecondaryAction = false,
	}) => {
		const closeModal = () => setIsOpen(false)
		const { clearExercises } = trainingConstructorStore
		const isEditMode = Boolean(onUpdate || onDelete)
		const headerTitle =
			titleProp ?? (isEditMode ? 'Изменение тренировки' : 'Создание тренировки')
		const mainButtonTitle =
			submitLabel ?? (isEditMode ? 'Сохранить изменения' : 'Создать тренировку')
		const [localTitle, setLocalTitle] = useState(prevTitle ?? '')
		const [localDescription, setLocalDescription] = useState(
			prevDescription ?? '',
		)
		const title = onDraftChange ? draftTitle ?? '' : localTitle
		const description = onDraftChange
			? draftDescription ?? ''
			: localDescription
		const [isDescriptionOpen, setIsDescriptionOpen] = useState(
			Boolean(draftDescription ?? prevDescription),
		)
		const [exerciseParams, setExerciseParams] =
			useState<ExerciseParams[]>(exercises)
		const [isExercisePickerOpen, setIsExercisePickerOpen] = useState(false)

		const { mutateAsync: createTraining, isLoading } = useCreateTraining()

		useEffect(() => {
			if (!isOpen || onDraftChange) {
				return
			}

			setLocalTitle(prevTitle ?? '')
			setLocalDescription(prevDescription ?? '')
			setIsDescriptionOpen(Boolean(prevDescription))
			setExerciseParams(exercises)
		}, [exercises, isOpen, onDraftChange, prevDescription, prevTitle])

		const updateTitle = (nextTitle: string) => {
			setLocalTitle(nextTitle)
			onDraftChange?.({ title: nextTitle, description })
		}

		const updateDescription = (nextDescription: string) => {
			setLocalDescription(nextDescription)
			onDraftChange?.({ title, description: nextDescription })
		}

		const canSubmit = useMemo(
			() => exerciseParams.length > 0 && title.trim().length > 0 && !isLoading,
			[exerciseParams.length, isLoading, title],
		)

		const deleteExercise = (exerciseId: number) => {
			setExerciseParams((prev) =>
				reorderExerciseParams(
					prev.filter((exercise) => exercise.exercise_id !== exerciseId),
				),
			)
		}

		const selectedExerciseIds = useMemo(
			() => new Set(exerciseParams.map((exercise) => exercise.exercise_id)),
			[exerciseParams],
		)

		const toggleExerciseFromPicker = (exercise: Exercise) => {
			setExerciseParams((prev) => {
				const isAlreadySelected = prev.some(
					(params) => params.exercise_id === exercise.id_exercise,
				)

				if (isAlreadySelected) {
					return reorderExerciseParams(
						prev.filter(
							(params) => params.exercise_id !== exercise.id_exercise,
						),
					)
				}

				return [
					...prev,
					buildExerciseParamsFromExercise(exercise, prev.length),
				]
			})
		}

		const clearModal = () => {
			setLocalTitle('')
			setLocalDescription('')
			setExerciseParams([])
			setIsExercisePickerOpen(false)
			onDraftChange?.({ title: '', description: '' })
			closeModal()
		}

		const clearTrainingCreation = () => {
			clearExercises()
			clearModal()
		}

		const handleCreateSuccess = (createdTraining: CreateTrainingResponse) => {
			onCreateSuccess?.(createdTraining)
			clearTrainingCreation()
		}

		const handleMainButtonClick = async () => {
			if (!canSubmit) {
				return
			}

			if (onUpdate) {
				onUpdate(exerciseParams, title.trim(), description.trim())
				closeModal()
				return
			}

			const createdTraining = await createTraining({
				title: title.trim(),
				description: description.trim(),
				exerciseParams,
			})
			handleCreateSuccess(createdTraining)
		}

		const handleClearClick = () => {
			if (onDelete) {
				onDelete()
				clearModal()
				return
			}

			clearTrainingCreation()
		}

		return (
			<Modal
				isOpen={isOpen}
				onRequestClose={closeModal}
				shouldCloseOnOverlayClick
				className="training-form-modal"
				overlayClassName="training-form-modal-overlay"
			>
				<motion.div
					className="training-form-sheet"
					initial={{ y: '100%', opacity: 0.96 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: '100%', opacity: 0.96 }}
					transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
				>
					<div className="training-form-handle" aria-hidden="true" />
					<header className="training-form-header">
						<h2>{headerTitle}</h2>
						<button
							type="button"
							className="training-form-close"
							onClick={closeModal}
							aria-label={
								isEditMode
									? 'Закрыть изменение тренировки'
									: 'Закрыть создание тренировки'
							}
						>
							<X aria-hidden="true" />
						</button>
					</header>

					<div className="training-form-content">
						<label className="training-form-title-field">
							<Bike aria-hidden="true" />
							<input
								value={title}
								maxLength={TITLE_LIMIT}
								onChange={(event) => updateTitle(event.target.value)}
								placeholder="Название тренировки"
								aria-label="Название тренировки"
							/>
							<span>{title.length}/{TITLE_LIMIT}</span>
						</label>

						<div className="training-form-description">
							{!isDescriptionOpen ? (
								<button
									type="button"
									onClick={() => setIsDescriptionOpen(true)}
									className="training-form-description-toggle"
								>
									<Plus aria-hidden="true" />
									<span>Добавить описание</span>
									<small>(необязательно)</small>
								</button>
							) : (
								<AnimatePresence initial={false}>
									<motion.label
										className="training-form-description-field"
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: 'auto', opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
									>
										<textarea
											value={description}
											maxLength={DESCRIPTION_LIMIT}
											onChange={(event) =>
												updateDescription(event.target.value)
											}
											placeholder="Описание тренировки"
											rows={3}
											aria-label="Описание тренировки"
										/>
										<span>{description.length}/{DESCRIPTION_LIMIT}</span>
									</motion.label>
								</AnimatePresence>
							)}
						</div>

						<section className="training-form-exercises">
							<div className="training-form-exercises-head">
								<h3>{formatExerciseCount(exerciseParams.length)}</h3>
							</div>
							<ExercisesAccordion
								onDelete={deleteExercise}
								exerciseParams={exerciseParams}
								setExerciseParams={setExerciseParams}
								addedExercises={exerciseParams}
								onAddExercise={() => setIsExercisePickerOpen(true)}
							/>
						</section>
					</div>

					<footer className="training-form-footer">
						<button
							type="button"
							className="training-form-primary"
							onClick={handleMainButtonClick}
							disabled={!canSubmit}
						>
							{isLoading ? 'Создаём...' : mainButtonTitle}
						</button>
						{!hideSecondaryAction && (
							<button
								type="button"
								className="training-form-secondary"
								onClick={handleClearClick}
							>
								{onDelete ? 'Удалить тренировку' : 'Очистить всё'}
							</button>
						)}
						<p>
							<Lock aria-hidden="true" />
							Тренировка будет сохранена только у вас
						</p>
					</footer>
					<ExercisePickerSheet
						isOpen={isExercisePickerOpen}
						onClose={() => setIsExercisePickerOpen(false)}
						selectedExerciseIds={selectedExerciseIds}
						onToggleExercise={toggleExerciseFromPicker}
					/>
				</motion.div>
			</Modal>
		)
	},
)
